import React from 'react';
import PropTypes from 'prop-types';

import KeyboardFormField from '@common/components/keyboardFormField';
import Checkbox from '@common/components/checkbox';
import { PasswordStrengthMeter } from '@app/dev/terminal/components/passwordChecker';
import { useTranslation } from '@common/hooks/translation';

/**
 * Generic step renderer.
 * Receives a `fields` array (from fieldRegistry, filtered & enabled by stepsConfig).
 * Checkbox fields and input fields are rendered in their own wrappers to preserve CSS layout,
 * so any combination of field types can coexist in a single step.
 */
export const DynamicStep = ({
  fields = [], form, setForm, errors,
}) => {
  const t = useTranslation();

  const onChange = (field, value) => {
    const extra = field.onAfterChange ? field.onAfterChange(value, form) : null;
    setForm({ ...form, [field.name]: value, ...extra });
  };

  const inputFields = fields.filter((f) => f.enabled && f.fieldType !== 'checkbox');
  const checkboxFields = fields.filter((f) => f.enabled && f.fieldType === 'checkbox');

  return (
    <div className="popup-register-wide-form">
      {inputFields.length > 0 && (
        <div className="input-wrapper">
          {inputFields.map((field) => {
            const FieldComponent = field.customViewComponent || KeyboardFormField;
            return (
              <div key={field.name} className={`register-single-input ${field.className || ''}`}>
                <div className="input-row">
                  <FieldComponent
                    readOnly={field.readOnly}
                    type={field.type}
                    name={field.fieldName}
                    value={form[field.name]}
                    onChange={(value) => onChange(field, value)}
                    errorMessage={errors[field.name]}
                    className={field.className}
                  />
                </div>
                {field.showStrengthMeter && (
                  <PasswordStrengthMeter password={form[field.name]} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {checkboxFields.length > 0 && (
        <div className="check-box">
          {checkboxFields.map((field) => (
            <Checkbox
              key={field.name}
              onChange={(e) => onChange(field, e.target.checked)}
              name={field.fieldName}
              checked={!!form[field.name]}
              id={field.fieldName}
              label={(
                <label className="check-box-label" htmlFor={field.fieldName}>
                  <span>{t(field.label)}</span>
                  <span className="error-message">{t(errors[field.name])}</span>
                </label>
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

DynamicStep.propTypes = {
  fields: PropTypes.array,
  form: PropTypes.object,
  setForm: PropTypes.func,
  errors: PropTypes.object,
};
